// typespecs/api_types.ts
import { extendZodWithOpenApi as extendZodWithOpenApi4 } from "@asteasolutions/zod-to-openapi";
import { z as z10 } from "zod";

// typespecs/app_types.ts
import { extendZodWithOpenApi as extendZodWithOpenApi3 } from "@asteasolutions/zod-to-openapi";
import { z as z8 } from "zod";

// typespecs/common_types.ts
import { z } from "zod";
import { extendZodWithOpenApi } from "@asteasolutions/zod-to-openapi";
extendZodWithOpenApi(z);
var literalSchema = z.union([
  z.string().openapi({ title: "string" }),
  z.number().openapi({ title: "number" }),
  z.boolean().openapi({ title: "boolean" }),
  z.null().openapi({ title: "null" })
]);
var jsonSchema = z.lazy(
  () => z.union([
    literalSchema,
    z.array(jsonSchema).openapi({ title: "array" }),
    z.record(jsonSchema).openapi({ title: "object" })
  ])
);
var datetimeStringSchema = z.string().transform((x, ctx) => {
  const d = new Date(x);
  if (isNaN(d.getTime())) {
    ctx.addIssue({
      code: z.ZodIssueCode.invalid_string,
      validation: "datetime",
      message: "Invalid datetime"
    });
    return z.NEVER;
  }
  return d.toISOString();
}).openapi({ format: "date-time" });
var objectTypes = z.enum([
  "project",
  "experiment",
  "dataset",
  "prompt",
  "prompt_session",
  "role",
  "group",
  "acl",
  "user",
  "project_score",
  "project_tag",
  "function",
  "view",
  "organization",
  "api_key"
]);
var objectTypesWithEvent = z.enum([
  "project",
  "experiment",
  "dataset",
  "prompt",
  "function",
  "prompt_session"
]);
function getEventObjectType(objectType) {
  return objectType === "project" ? "project_logs" : objectType;
}
function getEventObjectDescription(objectType) {
  return getEventObjectType(objectType).replace("_", " ");
}
function getObjectArticle(objectType) {
  return ["acl", "api_key", "experiment"].includes(objectType) ? "an" : "a";
}

// typespecs/custom_types.ts
import { extendZodWithOpenApi as extendZodWithOpenApi2 } from "@asteasolutions/zod-to-openapi";
import { z as z2 } from "zod";
extendZodWithOpenApi2(z2);
var modeSchema = z2.enum(["default", "stainless"]);
var _a;
var mode = modeSchema.parse(
  typeof process === "undefined" ? "default" : ((_a = process == null ? void 0 : process.env) == null ? void 0 : _a.BRAINTRUST_TYPESPECS_MODE) || "default"
);
var modeToTypes = {
  default: {
    any: z2.any(),
    literalTrue: z2.literal(true),
    literalFalse: z2.literal(false)
  },
  stainless: {
    // Stainless requires schemas which are completely permissive to be
    // tagged.
    any: z2.any().openapi({ ["x-stainless-any"]: true }),
    // Stainless does not support boolean literals in all SDKs.
    literalTrue: z2.boolean(),
    literalFalse: z2.boolean()
  }
};
var customTypes = modeToTypes[mode];
var customTypesMode = mode;

// typespecs/prompt.ts
import { z as z5 } from "zod";

// typespecs/openai/messages.ts
import { z as z3 } from "zod";
var messageRoleSchema = z3.enum([
  "system",
  "user",
  "assistant",
  "function",
  "tool",
  "model"
]);
var chatCompletionSystemMessageParamSchema = z3.object({
  content: z3.string().default(""),
  role: z3.literal("system"),
  name: z3.string().optional()
});
var chatCompletionContentPartTextSchema = z3.object({
  text: z3.string().default(""),
  type: z3.literal("text")
});
var imageURLSchema = z3.object({
  url: z3.string(),
  detail: z3.union([
    z3.literal("auto").openapi({ title: "auto" }),
    z3.literal("low").openapi({ title: "low" }),
    z3.literal("high").openapi({ title: "high" })
  ]).optional()
});
var chatCompletionContentPartImageSchema = z3.object({
  image_url: imageURLSchema,
  type: z3.literal("image_url")
});
var chatCompletionContentPartSchema = z3.union([
  chatCompletionContentPartTextSchema.openapi({ title: "text" }),
  chatCompletionContentPartImageSchema.openapi({ title: "image_url" })
]);
var chatCompletionContentSchema = z3.union([
  z3.string().default("").openapi({ title: "text" }),
  z3.array(chatCompletionContentPartSchema).openapi({ title: "array" })
]);
var chatCompletionUserMessageParamSchema = z3.object({
  content: chatCompletionContentSchema,
  role: z3.literal("user"),
  name: z3.string().optional()
});
var functionCallSchema = z3.object({
  arguments: z3.string(),
  name: z3.string()
});
var functionSchema = z3.object({
  arguments: z3.string(),
  name: z3.string()
});
var chatCompletionToolMessageParamSchema = z3.object({
  content: z3.string().default(""),
  role: z3.literal("tool"),
  tool_call_id: z3.string().default("")
});
var chatCompletionFunctionMessageParamSchema = z3.object({
  content: z3.string().default(""),
  name: z3.string(),
  role: z3.literal("function")
});
var chatCompletionMessageToolCallSchema = z3.object({
  id: z3.string(),
  function: functionSchema,
  type: z3.literal("function")
});
var chatCompletionAssistantMessageParamSchema = z3.object({
  role: z3.literal("assistant"),
  content: z3.string().nullish(),
  // NOTE: It's important to keep these optional, rather than nullish, to stay
  // inline with the OpenAI SDK's type definition.
  function_call: functionCallSchema.nullish().transform((x) => x != null ? x : void 0),
  name: z3.string().nullish().transform((x) => x != null ? x : void 0),
  tool_calls: z3.array(chatCompletionMessageToolCallSchema).nullish().transform((x) => x != null ? x : void 0)
});
var chatCompletionFallbackMessageParamSchema = z3.object({
  role: messageRoleSchema.exclude([
    "system",
    "user",
    "assistant",
    "tool",
    "function"
  ]),
  content: z3.string().nullish()
});
var chatCompletionOpenAIMessageParamSchema = z3.union([
  chatCompletionSystemMessageParamSchema.openapi({ title: "system" }),
  chatCompletionUserMessageParamSchema.openapi({ title: "user" }),
  chatCompletionAssistantMessageParamSchema.openapi({ title: "assistant" }),
  chatCompletionToolMessageParamSchema.openapi({ title: "tool" }),
  chatCompletionFunctionMessageParamSchema.openapi({ title: "function" })
]);
var chatCompletionMessageParamSchema = z3.union([
  chatCompletionOpenAIMessageParamSchema.openapi({ title: "openai" }),
  chatCompletionFallbackMessageParamSchema.openapi({ title: "fallback" })
]);

// typespecs/openai/tools.ts
import { z as z4 } from "zod";
var functionParametersSchema = z4.record(z4.unknown());
var functionDefinitionSchema = z4.object({
  name: z4.string(),
  description: z4.string().optional(),
  parameters: functionParametersSchema.optional()
});
var chatCompletionToolSchema = z4.object({
  function: functionDefinitionSchema,
  type: z4.literal("function")
});
var toolsSchema = z4.array(chatCompletionToolSchema);

// typespecs/prompt.ts
var promptBlockDataSchema = z5.union([
  z5.object({
    type: z5.literal("completion"),
    content: z5.string()
  }).openapi({ title: "completion" }),
  z5.object({
    type: z5.literal("chat"),
    messages: z5.array(chatCompletionMessageParamSchema),
    tools: z5.string().optional()
  }).openapi({ title: "chat" })
]);
var braintrustModelParamsSchema = z5.object({
  use_cache: z5.boolean().optional()
});
var BRAINTRUST_PARAMS = Object.keys(braintrustModelParamsSchema.shape);
var openAIModelParamsSchema = z5.object({
  temperature: z5.number().optional(),
  top_p: z5.number().optional(),
  max_tokens: z5.number().optional(),
  frequency_penalty: z5.number().optional(),
  presence_penalty: z5.number().optional(),
  response_format: z5.object({ type: z5.literal("json_object") }).nullish(),
  tool_choice: z5.union([
    z5.literal("auto").openapi({ title: "auto" }),
    z5.literal("none").openapi({ title: "none" }),
    z5.object({
      type: z5.literal("function"),
      function: z5.object({ name: z5.string() })
    }).openapi({ title: "function" })
  ]).optional(),
  function_call: z5.union([
    z5.literal("auto").openapi({ title: "auto" }),
    z5.literal("none").openapi({ title: "none" }),
    z5.object({
      name: z5.string()
    }).openapi({ title: "function" })
  ]).optional(),
  n: z5.number().optional(),
  stop: z5.array(z5.string()).optional()
});
var anthropicModelParamsSchema = z5.object({
  max_tokens: z5.number(),
  temperature: z5.number(),
  top_p: z5.number().optional(),
  top_k: z5.number().optional(),
  stop_sequences: z5.array(z5.string()).optional(),
  max_tokens_to_sample: z5.number().optional().describe("This is a legacy parameter that should not be used.")
});
var googleModelParamsSchema = z5.object({
  temperature: z5.number().optional(),
  maxOutputTokens: z5.number().optional(),
  topP: z5.number().optional(),
  topK: z5.number().optional()
});
var windowAIModelParamsSchema = z5.object({
  temperature: z5.number().optional(),
  topK: z5.number().optional()
});
var jsCompletionParamsSchema = z5.object({});
var modelParamsSchema = z5.union([
  braintrustModelParamsSchema.merge(openAIModelParamsSchema).passthrough().openapi({ title: "OpenAIModelParams" }),
  braintrustModelParamsSchema.merge(anthropicModelParamsSchema).passthrough().openapi({ title: "AnthropicModelParams" }),
  braintrustModelParamsSchema.merge(googleModelParamsSchema).passthrough().openapi({ title: "GoogleModelParams" }),
  braintrustModelParamsSchema.merge(windowAIModelParamsSchema).passthrough().openapi({ title: "WindowAIModelParams" }),
  braintrustModelParamsSchema.merge(jsCompletionParamsSchema).passthrough().openapi({ title: "JsCompletionParams" })
]);
var anyModelParamsSchema = openAIModelParamsSchema.merge(anthropicModelParamsSchema).merge(googleModelParamsSchema).merge(braintrustModelParamsSchema);
var promptOptionsSchema = z5.object({
  model: z5.string().optional(),
  params: modelParamsSchema.optional(),
  position: z5.string().optional()
});
var promptDataSchema = z5.object({
  prompt: promptBlockDataSchema.nullish(),
  options: promptOptionsSchema.nullish(),
  // This should be a union once we support multiple parser types
  parser: z5.object({
    type: z5.literal("llm_classifier"),
    use_cot: z5.boolean(),
    choice_scores: z5.record(z5.number().min(0).max(1))
  }).nullish(),
  origin: z5.object({
    prompt_id: z5.string().optional(),
    project_id: z5.string().optional(),
    prompt_version: z5.string().optional()
  }).nullish()
}).openapi("PromptData");

// typespecs/view.ts
import { z as z6 } from "zod";
var viewTypeEnum = z6.enum([
  "projects",
  "logs",
  "experiments",
  "datasets",
  "prompts",
  "playgrounds",
  "experiment",
  "dataset"
]).describe("Type of table that the view corresponds to.");
var viewDataSearchSchema = z6.object({
  filter: z6.array(customTypes.any).nullish(),
  tag: z6.array(customTypes.any).nullish(),
  match: z6.array(customTypes.any).nullish(),
  sort: z6.array(customTypes.any).nullish()
}).strip().openapi("ViewDataSearch");
var viewDataSchema = z6.object({
  search: viewDataSearchSchema.nullish()
}).strip().openapi("ViewData");
var viewOptionsSchema = z6.object({
  columnVisibility: z6.record(z6.boolean()).nullish(),
  columnOrder: z6.array(z6.string()).nullish(),
  columnSizing: z6.record(z6.number()).nullish()
}).strip().openapi("ViewOptions");

// typespecs/functions.ts
import { z as z7 } from "zod";
var validRuntimesEnum = z7.enum(["node", "python"]);
var runtimeContextSchema = z7.object({
  runtime: validRuntimesEnum,
  version: z7.string()
});
var functionIdSchema = z7.union([
  z7.object({
    function_id: z7.string().describe("The ID of the function"),
    version: z7.string().optional().describe("The version of the function")
  }).describe("Function id"),
  z7.object({
    project_name: z7.string().describe("The name of the project containing the function"),
    slug: z7.string().describe("The slug of the function"),
    version: z7.string().optional().describe("The version of the function")
  }).describe("Project name and slug"),
  z7.object({
    global_function: z7.string().describe(
      "The name of the global function. Currently, the global namespace includes the functions in autoevals"
    )
  }).describe("Global function name"),
  z7.object({
    prompt_session_id: z7.string().describe("The ID of the prompt session"),
    prompt_session_function_id: z7.string().describe("The ID of the function in the prompt session"),
    version: z7.string().optional().describe("The version of the function")
  }).describe("Prompt session id"),
  z7.object({
    inline_context: runtimeContextSchema,
    code: z7.string().describe("The inline code to execute")
  }).describe("Inline code function")
]).describe("Options for identifying a function");
var useFunctionSchema = functionIdSchema;
var invokeFunctionNonIdArgsSchema = z7.object({
  input: z7.any().optional().describe(
    "Argument to the function, which can be any JSON serializable value"
  ),
  parent: z7.union([
    z7.object({
      object_type: z7.enum(["project_logs", "experiment"]),
      object_id: z7.string().describe("The id of the container object you are logging to")
    }).describe("Object type and id"),
    z7.string().optional().describe(
      "The parent's span identifier, created by calling `.export()` on a span"
    )
  ]).describe("Options for tracing the function call"),
  stream: z7.boolean().optional().describe(
    "Whether to stream the response. If true, results will be returned in the Braintrust SSE format."
  )
});
var invokeFunctionSchema = functionIdSchema.and(
  invokeFunctionNonIdArgsSchema
);
var invokeApiSchema = invokeFunctionNonIdArgsSchema.merge(
  z7.object({
    version: z7.string().optional().describe("The version of the function")
  })
).describe("The request to invoke a function");
var runEvalSchema = z7.object({
  project_id: z7.string().describe("Unique identifier for the project to run the eval in"),
  data: z7.union([
    z7.object({
      dataset_id: z7.string()
    }).describe("Dataset id"),
    z7.object({
      project_name: z7.string(),
      dataset_name: z7.string()
    }).describe("Project and dataset name")
  ]).describe("The dataset to use"),
  task: functionIdSchema.describe("The function to evaluate"),
  scores: z7.array(functionIdSchema).describe("The functions to score the eval on"),
  experiment_name: z7.string().optional().describe(
    "An optional name for the experiment created by this eval. If it conflicts with an existing experiment, it will be suffixed with a unique identifier."
  ),
  metadata: z7.record(z7.unknown()).optional().describe(
    "Optional experiment-level metadata to store about the evaluation. You can later use this to slice & dice across experiments."
  ),
  stream: z7.boolean().optional().describe(
    "Whether to stream the results of the eval. If true, the request will return two events: one to indicate the experiment has started, and another upon completion. If false, the request will return the evaluation's summary upon completion."
  )
});
var baseSSEEventSchema = z7.object({
  id: z7.string().optional(),
  data: z7.string()
});
var sseTextEventSchema = baseSSEEventSchema.merge(
  z7.object({
    event: z7.literal("text_delta")
  })
);
var sseDataEventSchema = baseSSEEventSchema.merge(
  z7.object({
    event: z7.literal("json_delta")
  })
);
var sseDoneEventSchema = baseSSEEventSchema.omit({ data: true }).merge(
  z7.object({
    event: z7.literal("done"),
    data: z7.literal("")
  })
);
var callEventSchema = z7.union([
  sseTextEventSchema.openapi({ title: "text_delta" }),
  sseDataEventSchema.openapi({ title: "json_delta" }),
  sseDoneEventSchema.openapi({ title: "done" })
]);

// typespecs/app_types.ts
extendZodWithOpenApi3(z8);
function generateBaseTableSchema(objectName, opts) {
  let nameDescription = `Name of the ${objectName}`;
  if (opts == null ? void 0 : opts.uniqueName) {
    nameDescription += `. Within a project, ${objectName} names are unique`;
  }
  return z8.object({
    id: z8.string().uuid().describe(`Unique identifier for the ${objectName}`),
    project_id: z8.string().uuid().describe(
      `Unique identifier for the project that the ${objectName} belongs under`
    ),
    name: z8.string().describe(nameDescription),
    description: z8.string().nullish().describe(`Textual description of the ${objectName}`),
    created: datetimeStringSchema.nullish().describe(`Date of ${objectName} creation`),
    deleted_at: datetimeStringSchema.nullish().describe(
      `Date of ${objectName} deletion, or null if the ${objectName} is still active`
    ),
    user_id: z8.string().uuid().nullish().describe(`Identifies the user who created the ${objectName}`),
    metadata: z8.record(customTypes.any).nullish().describe(`User-controlled metadata about the ${objectName}`)
  });
}
var aclObjectTypeEnum = z8.enum([
  "organization",
  "project",
  "experiment",
  "dataset",
  "prompt",
  "prompt_session",
  "group",
  "role",
  "org_member",
  "project_log",
  "org_project"
]).describe("The object type that the ACL applies to");
var userBaseSchema = generateBaseTableSchema("user");
var userSchema = z8.object({
  id: userBaseSchema.shape.id,
  given_name: z8.string().nullish().describe("Given name of the user"),
  family_name: z8.string().nullish().describe("Family name of the user"),
  email: z8.string().nullish().describe("The user's email"),
  avatar_url: z8.string().nullish().describe("URL of the user's Avatar image"),
  created: userBaseSchema.shape.created
}).openapi("User");
var organizationBaseSchema = generateBaseTableSchema("organization");
var organizationSchema = z8.object({
  id: organizationBaseSchema.shape.id,
  name: organizationBaseSchema.shape.name,
  api_url: z8.string().nullish(),
  is_universal_api: z8.boolean().nullish(),
  proxy_url: z8.string().nullish(),
  realtime_url: z8.string().nullish(),
  created: organizationBaseSchema.shape.created
}).openapi("Organization");
var maxOverWindowSchema = z8.object({
  window_size_days: z8.number().int().positive(),
  max_value: z8.number().nonnegative()
}).openapi("MaxOverWindow");
var resourcesSchema = z8.object({
  org_id: organizationSchema.shape.id,
  forbid_toggle_experiment_public_to_private: z8.boolean().nullish(),
  num_private_experiment_row_actions: maxOverWindowSchema.nullish(),
  forbid_insert_datasets: z8.boolean().nullish(),
  forbid_insert_prompt_sessions: z8.boolean().nullish(),
  forbid_access_sql_explorer: z8.boolean().nullish(),
  num_production_log_row_actions: maxOverWindowSchema.nullish(),
  num_dataset_row_actions: maxOverWindowSchema.nullish()
}).openapi("Resources");
var memberSchema = z8.object({
  org_id: organizationSchema.shape.id,
  user_id: userSchema.shape.id
}).openapi("Member");
var orgSecretsBaseSchema = generateBaseTableSchema("org secrets");
var orgSecretsSchema = z8.object({
  id: orgSecretsBaseSchema.shape.id,
  created: orgSecretsBaseSchema.shape.created,
  key_id: z8.string().uuid(),
  org_id: organizationSchema.shape.id,
  name: orgSecretsBaseSchema.shape.name,
  secret: z8.string().nullish(),
  type: z8.string().nullish(),
  metadata: customTypes.any
}).openapi("OrgSecrets");
var apiKeyBaseSchema = generateBaseTableSchema("api key");
var apiKeySchema = z8.object({
  id: apiKeyBaseSchema.shape.id,
  created: apiKeyBaseSchema.shape.created,
  name: apiKeyBaseSchema.shape.name,
  preview_name: z8.string(),
  user_id: userSchema.shape.id.nullish(),
  org_id: organizationSchema.shape.id.nullish()
}).openapi("ApiKey");
var projectSettingsSchema = z8.object({
  comparison_key: z8.string().nullish().describe("The key used to join two experiments (defaults to `input`).")
});
var projectBaseSchema = generateBaseTableSchema("project");
var projectSchema = z8.object({
  id: projectBaseSchema.shape.id,
  org_id: z8.string().uuid().describe(
    "Unique id for the organization that the project belongs under"
  ),
  name: projectBaseSchema.shape.name,
  created: projectBaseSchema.shape.created,
  deleted_at: projectBaseSchema.shape.deleted_at,
  user_id: projectBaseSchema.shape.user_id,
  settings: projectSettingsSchema.nullish()
}).openapi("Project");
var datasetBaseSchema = generateBaseTableSchema("dataset", {
  uniqueName: true
});
var datasetSchema = z8.object({
  id: datasetBaseSchema.shape.id,
  project_id: datasetBaseSchema.shape.project_id.nullish(),
  name: datasetBaseSchema.shape.name,
  description: datasetBaseSchema.shape.description,
  created: datasetBaseSchema.shape.created,
  deleted_at: datasetBaseSchema.shape.deleted_at,
  user_id: datasetBaseSchema.shape.user_id,
  metadata: datasetBaseSchema.shape.metadata
}).openapi("Dataset");
var promptLogIdLiteralSchema = z8.literal("p").describe("A literal 'p' which identifies the object as a project prompt");
var promptBaseSchema = generateBaseTableSchema("prompt");
var promptSchemaObject = z8.object({
  id: promptBaseSchema.shape.id,
  // This has to be copy/pasted because zod blows up when there are circular dependencies
  _xact_id: z8.string().describe(
    `The transaction id of an event is unique to the network operation that processed the event insertion. Transaction ids are monotonically increasing over time and can be used to retrieve a versioned snapshot of the prompt (see the \`version\` parameter)`
  ),
  project_id: promptBaseSchema.shape.project_id,
  log_id: promptLogIdLiteralSchema,
  org_id: organizationSchema.shape.id,
  name: promptBaseSchema.shape.name,
  slug: z8.string().describe("Unique identifier for the prompt"),
  description: promptBaseSchema.shape.description,
  created: promptBaseSchema.shape.created,
  prompt_data: promptDataSchema.nullish().describe("The prompt, model, and its parameters"),
  tags: z8.array(z8.string()).nullish().describe("A list of tags for the prompt"),
  metadata: promptBaseSchema.shape.metadata,
  // An empty (unspecified) function_type is equivalent to "dynamic".
  function_type: z8.enum(["task", "llm", "scorer"]).nullish()
});
var promptSchema = promptSchemaObject.openapi("Prompt");
var codeBundleSchema = z8.object({
  runtime_context: runtimeContextSchema,
  location: z8.object({
    type: z8.literal("experiment"),
    eval_name: z8.string(),
    position: z8.union([
      z8.object({ type: z8.literal("task") }),
      z8.object({ type: z8.literal("scorer"), index: z8.number() }).openapi({ title: "scorer" })
    ])
  }),
  bundle_id: z8.string(),
  preview: z8.string().nullish().describe("A preview of the code")
});
var functionDataSchema = z8.union([
  z8.object({
    type: z8.literal("prompt")
    // For backwards compatibility reasons, the prompt definition is hoisted out and stored
    // in the outer object
  }).openapi({ title: "prompt" }),
  z8.object({
    type: z8.literal("code"),
    data: z8.union([
      z8.object({
        type: z8.literal("bundle")
      }).and(codeBundleSchema),
      z8.object({
        type: z8.literal("inline"),
        runtime_context: runtimeContextSchema,
        code: z8.string()
      })
    ])
  }).openapi({ title: "code" }),
  z8.object({
    type: z8.literal("global"),
    name: z8.string()
  }).openapi({ title: "global" })
]);
var functionSchema2 = promptSchemaObject.merge(
  z8.object({
    function_data: functionDataSchema,
    origin: z8.object({
      object_type: aclObjectTypeEnum,
      object_id: z8.string().uuid().describe("Id of the object the function is originating from"),
      internal: z8.boolean().nullish().describe(
        "The function exists for internal purposes and should not be displayed in the list of functions."
      )
    }).nullish()
  })
).openapi("Function");
var repoInfoSchema = z8.object({
  commit: z8.string().nullish().describe("SHA of most recent commit"),
  branch: z8.string().nullish().describe("Name of the branch the most recent commit belongs to"),
  tag: z8.string().nullish().describe("Name of the tag on the most recent commit"),
  dirty: z8.boolean().nullish().describe(
    "Whether or not the repo had uncommitted changes when snapshotted"
  ),
  author_name: z8.string().nullish().describe("Name of the author of the most recent commit"),
  author_email: z8.string().nullish().describe("Email of the author of the most recent commit"),
  commit_message: z8.string().nullish().describe("Most recent commit message"),
  commit_time: z8.string().nullish().describe("Time of the most recent commit"),
  git_diff: z8.string().nullish().describe(
    "If the repo was dirty when run, this includes the diff between the current state of the repo and the most recent commit."
  )
}).describe(
  "Metadata about the state of the repo when the experiment was created"
).openapi("RepoInfo");
var experimentBaseSchema = generateBaseTableSchema("experiment", {
  uniqueName: true
});
var experimentSchema = z8.object({
  id: experimentBaseSchema.shape.id,
  project_id: experimentBaseSchema.shape.project_id,
  name: experimentBaseSchema.shape.name,
  description: experimentBaseSchema.shape.description,
  created: experimentBaseSchema.shape.created,
  repo_info: repoInfoSchema.nullish(),
  commit: z8.string().nullish().describe("Commit, taken directly from `repo_info.commit`"),
  base_exp_id: z8.string().uuid().nullish().describe(
    "Id of default base experiment to compare against when viewing this experiment"
  ),
  deleted_at: experimentBaseSchema.shape.deleted_at,
  dataset_id: z8.string().uuid().nullish().describe(
    "Identifier of the linked dataset, or null if the experiment is not linked to a dataset"
  ),
  dataset_version: z8.string().nullish().describe(
    "Version number of the linked dataset the experiment was run against. This can be used to reproduce the experiment after the dataset has been modified."
  ),
  public: z8.boolean().describe(
    "Whether or not the experiment is public. Public experiments can be viewed by anybody inside or outside the organization"
  ),
  user_id: experimentBaseSchema.shape.user_id,
  metadata: experimentBaseSchema.shape.metadata
}).openapi("Experiment");
var promptSessionBaseSchema = generateBaseTableSchema("promptSession", {
  uniqueName: true
});
var promptSessionSchema = z8.object({
  id: promptSessionBaseSchema.shape.id,
  name: promptSessionBaseSchema.shape.name,
  description: promptSessionBaseSchema.shape.description,
  created: promptSessionBaseSchema.shape.created,
  deleted_at: promptSessionBaseSchema.shape.deleted_at,
  user_id: promptSessionBaseSchema.shape.user_id,
  project_id: promptSessionBaseSchema.shape.project_id,
  org_id: organizationSchema.shape.id.nullish().describe(
    "This field is deprecated and will be removed in a future revision"
  )
}).openapi("PromptSession");
var permissionEnum = z8.enum([
  "create",
  "read",
  "update",
  "delete",
  "create_acls",
  "read_acls",
  "update_acls",
  "delete_acls"
]).describe(
  [
    "Each permission permits a certain type of operation on an object in the system",
    "Permissions can be assigned to to objects on an individual basis, or grouped into roles"
  ].join("\n\n")
);
var roleBaseSchema = generateBaseTableSchema("role");
var roleSchema = z8.object({
  id: roleBaseSchema.shape.id,
  org_id: z8.string().uuid().nullish().describe(
    [
      "Unique id for the organization that the role belongs under",
      "A null org_id indicates a system role, which may be assigned to anybody and inherited by any other role, but cannot be edited.",
      "It is forbidden to change the org after creating a role"
    ].join("\n\n")
  ),
  user_id: roleBaseSchema.shape.user_id,
  created: roleBaseSchema.shape.created,
  name: roleBaseSchema.shape.name,
  description: roleBaseSchema.shape.description,
  deleted_at: roleBaseSchema.shape.deleted_at,
  member_permissions: z8.array(
    z8.object({
      permission: permissionEnum,
      restrict_object_type: aclObjectTypeEnum.nullish()
    })
  ).nullish().describe(
    "(permission, restrict_object_type) tuples which belong to this role"
  ),
  member_roles: z8.array(z8.string().uuid()).nullish().describe(
    [
      "Ids of the roles this role inherits from",
      "An inheriting role has all the permissions contained in its member roles, as well as all of their inherited permissions"
    ].join("\n\n")
  )
}).describe(
  [
    "A role is a collection of permissions which can be granted as part of an ACL",
    "Roles can consist of individual permissions, as well as a set of roles they inherit from"
  ].join("\n\n")
).openapi("Role");
var groupBaseSchema = generateBaseTableSchema("group");
var groupSchema = z8.object({
  id: groupBaseSchema.shape.id,
  org_id: z8.string().uuid().describe(
    [
      "Unique id for the organization that the group belongs under",
      "It is forbidden to change the org after creating a group"
    ].join("\n\n")
  ),
  user_id: groupBaseSchema.shape.user_id,
  created: groupBaseSchema.shape.created,
  name: groupBaseSchema.shape.name,
  description: groupBaseSchema.shape.description,
  deleted_at: groupBaseSchema.shape.deleted_at,
  member_users: z8.array(z8.string().uuid()).nullish().describe("Ids of users which belong to this group"),
  member_groups: z8.array(z8.string().uuid()).nullish().describe(
    [
      "Ids of the groups this group inherits from",
      "An inheriting group has all the users contained in its member groups, as well as all of their inherited users"
    ].join("\n\n")
  )
}).describe(
  [
    "A group is a collection of users which can be assigned an ACL",
    "Groups can consist of individual users, as well as a set of groups they inherit from"
  ].join("\n\n")
).openapi("Group");
var projectScoreTypeEnum = z8.enum(["slider", "categorical", "weighted", "minimum"]).describe("The type of the configured score");
var projectScoreCategory = z8.object({
  name: z8.string().describe("Name of the category"),
  value: z8.number().describe(
    "Numerical value of the category. Must be between 0 and 1, inclusive"
  )
}).describe("For categorical-type project scores, defines a single category").openapi("ProjectScoreCategory");
var projectScoreBaseSchema = generateBaseTableSchema("project score");
var projectScoreSchema = z8.object({
  id: projectScoreBaseSchema.shape.id,
  project_id: projectScoreBaseSchema.shape.project_id,
  user_id: projectScoreBaseSchema.shape.user_id.unwrap().unwrap(),
  created: projectScoreBaseSchema.shape.created,
  name: projectScoreBaseSchema.shape.name,
  description: projectScoreBaseSchema.shape.description,
  score_type: projectScoreTypeEnum,
  categories: z8.union([
    projectScoreCategory.array().describe(
      "For categorical-type project scores, the list of all categories"
    ).openapi({ title: "categorical" }),
    z8.record(z8.number()).describe(
      "For weighted-type project scores, the weights of each score"
    ).openapi({ title: "weighted" }),
    z8.array(z8.string()).describe(
      "For minimum-type project scores, the list of included scores"
    ).openapi({ title: "minimum" })
  ]).nullish(),
  config: z8.object({
    multi_select: z8.boolean().nullish(),
    destination: z8.literal("expected").nullish()
  }).nullish(),
  position: z8.string().nullish().describe(
    "An optional LexoRank-based string that sets the sort position for the score in the UI"
  )
}).describe(
  "A project score is a user-configured score, which can be manually-labeled through the UI"
).openapi("ProjectScore");
var projectTagBaseSchema = generateBaseTableSchema("project tag");
var projectTagSchema = z8.object({
  id: projectTagBaseSchema.shape.id,
  project_id: projectTagBaseSchema.shape.project_id,
  user_id: projectTagBaseSchema.shape.user_id.unwrap().unwrap(),
  created: projectTagBaseSchema.shape.created,
  name: projectTagBaseSchema.shape.name,
  description: projectTagBaseSchema.shape.description,
  color: z8.string().nullish().describe("Color of the tag for the UI")
}).describe(
  "A project tag is a user-configured tag for tracking and filtering your experiments, logs, and other data"
).openapi("ProjectTag");
var viewBaseSchema = generateBaseTableSchema("view");
var viewSchema = z8.object({
  id: viewBaseSchema.shape.id,
  object_type: aclObjectTypeEnum,
  object_id: z8.string().uuid().describe("The id of the object the view applies to"),
  view_type: viewTypeEnum,
  name: viewBaseSchema.shape.name,
  created: viewBaseSchema.shape.created,
  view_data: viewDataSchema.nullish().describe("The view definition"),
  options: viewOptionsSchema.nullish().describe("Options for the view in the app"),
  user_id: viewBaseSchema.shape.user_id,
  deleted_at: roleBaseSchema.shape.deleted_at
}).openapi("View");
var aclBaseSchema = generateBaseTableSchema("acl");
var aclSchema = z8.object({
  id: aclBaseSchema.shape.id,
  object_type: aclObjectTypeEnum,
  object_id: z8.string().uuid().describe("The id of the object the ACL applies to"),
  user_id: z8.string().uuid().nullish().describe(
    "Id of the user the ACL applies to. Exactly one of `user_id` and `group_id` will be provided"
  ),
  group_id: z8.string().uuid().nullish().describe(
    "Id of the group the ACL applies to. Exactly one of `user_id` and `group_id` will be provided"
  ),
  permission: permissionEnum.nullish().describe(
    "Permission the ACL grants. Exactly one of `permission` and `role_id` will be provided"
  ),
  restrict_object_type: aclObjectTypeEnum.nullish().describe(
    "When setting a permission directly, optionally restricts the permission grant to just the specified object type. Cannot be set alongside a `role_id`."
  ),
  role_id: z8.string().uuid().nullish().describe(
    "Id of the role the ACL grants. Exactly one of `permission` and `role_id` will be provided"
  ),
  _object_org_id: z8.string().uuid().describe("The organization the ACL's referred object belongs to"),
  created: aclBaseSchema.shape.created
}).describe(
  [
    "An ACL grants a certain permission or role to a certain user or group on an object.",
    "ACLs are inherited across the object hierarchy. So for example, if a user has read permissions on a project, they will also have read permissions on any experiment, dataset, etc. created within that project.",
    "To restrict a grant to a particular sub-object, you may specify `restrict_object_type` in the ACL, as part of a direct permission grant or as part of a role."
  ].join("\n\n")
).openapi("Acl");
var appLimitSchema = z8.number().int().nonnegative().describe("Limit the number of objects to return");
function generateBaseTableOpSchema(objectName) {
  return z8.object({
    org_name: z8.string().nullish().describe(
      `For nearly all users, this parameter should be unnecessary. But in the rare case that your API key belongs to multiple organizations, you may specify the name of the organization the ${objectName} belongs in.`
    )
  });
}
var startingAfterSchema = z8.string().uuid().describe(
  [
    "Pagination cursor id.",
    "For example, if the final item in the last page you fetched had an id of `foo`, pass `starting_after=foo` to fetch the next page. Note: you may only pass one of `starting_after` and `ending_before`"
  ].join("\n\n")
).openapi("StartingAfter");
var endingBeforeSchema = z8.string().uuid().describe(
  [
    "Pagination cursor id.",
    "For example, if the initial item in the last page you fetched had an id of `foo`, pass `ending_before=foo` to fetch the previous page. Note: you may only pass one of `starting_after` and `ending_before`"
  ].join("\n\n")
).openapi("EndingBefore");
function makeObjectIdsFilterSchema(objectName) {
  const item = z8.string().uuid();
  return z8.union([item, item.array()]).describe(
    `Filter search results to a particular set of ${objectName} IDs. To specify a list of IDs, include the query param multiple times`
  ).openapi(`${objectName}IdsFilter`);
}
var createProjectBaseSchema = generateBaseTableOpSchema("project");
var createProjectSchema = z8.object({
  name: projectSchema.shape.name,
  org_name: createProjectBaseSchema.shape.org_name
}).openapi("CreateProject");
var patchProjectSchema = z8.object({
  name: projectSchema.shape.name.nullish(),
  settings: projectSchema.shape.settings.describe(
    "Project settings. Patch operations replace all settings, so make sure you include all settings you want to keep."
  ).nullish()
}).openapi("PatchProject");
var createExperimentSchema = z8.object({
  project_id: experimentSchema.shape.project_id,
  name: experimentSchema.shape.name.nullish(),
  description: experimentSchema.shape.description,
  repo_info: experimentSchema.shape.repo_info,
  base_exp_id: experimentSchema.shape.base_exp_id,
  dataset_id: experimentSchema.shape.dataset_id,
  dataset_version: experimentSchema.shape.dataset_version,
  public: experimentSchema.shape.public.nullish(),
  metadata: experimentSchema.shape.metadata,
  ensure_new: z8.boolean().nullish().describe(
    "Normally, creating an experiment with the same name as an existing experiment will return the existing one un-modified. But if `ensure_new` is true, registration will generate a new experiment with a unique name in case of a conflict."
  )
}).openapi("CreateExperiment");
var patchExperimentSchema = createExperimentSchema.omit({ project_id: true, ensure_new: true }).openapi("PatchExperiment");
var createDatasetSchema = z8.object({
  project_id: datasetSchema.shape.project_id,
  name: datasetSchema.shape.name,
  description: datasetSchema.shape.description
}).openapi("CreateDataset");
var patchDatasetSchema = z8.object({
  name: datasetSchema.shape.name.nullish(),
  description: datasetSchema.shape.description,
  metadata: datasetSchema.shape.metadata
}).openapi("PatchDataset");
var createPromptSchema = promptSchema.omit({
  id: true,
  _xact_id: true,
  org_id: true,
  log_id: true,
  created: true,
  metadata: true
}).openapi("CreatePrompt");
var createFunctionSchema = functionSchema2.omit({
  id: true,
  _xact_id: true,
  org_id: true,
  log_id: true,
  created: true,
  metadata: true
}).openapi("CreateFunction");
var patchPromptSchema = z8.object({
  name: promptSchema.shape.name.nullish(),
  slug: promptSchema.shape.slug.nullish(),
  description: promptSchema.shape.description.nullish(),
  prompt_data: promptSchema.shape.prompt_data.nullish(),
  tags: promptSchema.shape.tags.nullish()
}).openapi("PatchPrompt");
var patchFunctionSchema = z8.object({
  name: functionSchema2.shape.name.nullish(),
  description: functionSchema2.shape.description.nullish(),
  prompt_data: functionSchema2.shape.prompt_data.nullish(),
  function_data: functionSchema2.shape.function_data.nullish(),
  tags: functionSchema2.shape.tags.nullish()
}).openapi("PatchFunction");
var createRoleBaseSchema = generateBaseTableOpSchema("role");
var createRoleSchema = z8.object({
  name: roleSchema.shape.name,
  description: roleSchema.shape.description,
  member_permissions: roleSchema.shape.member_permissions,
  member_roles: roleSchema.shape.member_roles,
  org_name: createRoleBaseSchema.shape.org_name
}).openapi("CreateRole");
var patchRoleSchema = createRoleSchema.omit({
  name: true,
  org_name: true,
  member_permissions: true,
  member_roles: true
}).merge(
  z8.object({
    name: createRoleSchema.shape.name.nullish(),
    add_member_permissions: roleSchema.shape.member_permissions.nullish().describe("A list of permissions to add to the role"),
    remove_member_permissions: roleSchema.shape.member_permissions.nullish().describe("A list of permissions to remove from the role"),
    add_member_roles: roleSchema.shape.member_roles.nullish().describe(
      "A list of role IDs to add to the role's inheriting-from set"
    ),
    remove_member_roles: roleSchema.shape.member_roles.nullish().describe(
      "A list of role IDs to remove from the role's inheriting-from set"
    )
  })
).openapi("PatchRole");
var createGroupBaseSchema = generateBaseTableOpSchema("group");
var createGroupSchema = z8.object({
  name: groupSchema.shape.name,
  description: groupSchema.shape.description,
  member_users: groupSchema.shape.member_users,
  member_groups: groupSchema.shape.member_groups,
  org_name: createGroupBaseSchema.shape.org_name
}).openapi("CreateGroup");
var patchGroupSchema = createGroupSchema.omit({ name: true, org_name: true, member_users: true, member_groups: true }).merge(
  z8.object({
    name: createGroupSchema.shape.name.nullish(),
    add_member_users: groupSchema.shape.member_users.nullish().describe("A list of user IDs to add to the group"),
    remove_member_users: groupSchema.shape.member_users.nullish().describe("A list of user IDs to remove from the group"),
    add_member_groups: groupSchema.shape.member_groups.nullish().describe(
      "A list of group IDs to add to the group's inheriting-from set"
    ),
    remove_member_groups: groupSchema.shape.member_groups.nullish().describe(
      "A list of group IDs to remove from the group's inheriting-from set"
    )
  })
).openapi("PatchGroup");
var aclItemSchema = aclSchema.omit({
  id: true,
  created: true,
  _object_org_id: true
}).openapi("AclItem");
var aclBatchUpdateRequestSchema = z8.object({
  add_acls: aclItemSchema.array().nullish(),
  remove_acls: aclItemSchema.array().nullish()
});
var aclBatchUpdateResponseSchema = z8.object({
  added_acls: aclSchema.array(),
  removed_acls: aclSchema.array()
});
var createProjectScoreSchema = z8.object({
  project_id: projectScoreSchema.shape.project_id,
  name: projectScoreSchema.shape.name,
  description: projectScoreSchema.shape.description,
  score_type: projectScoreSchema.shape.score_type,
  categories: projectScoreSchema.shape.categories
}).openapi("CreateProjectScore");
var patchProjectScoreSchema = z8.object({
  name: projectScoreSchema.shape.name.nullish(),
  description: projectScoreSchema.shape.description,
  score_type: projectScoreSchema.shape.score_type.nullish(),
  categories: projectScoreSchema.shape.categories
}).openapi("PatchProjectScore");
var createProjectTagSchema = z8.object({
  project_id: projectTagSchema.shape.project_id,
  name: projectTagSchema.shape.name,
  description: projectTagSchema.shape.description,
  color: projectTagSchema.shape.color
}).openapi("CreateProjectTag");
var patchProjectTagSchema = z8.object({
  name: projectTagSchema.shape.name.nullish(),
  description: projectTagSchema.shape.description,
  color: projectTagSchema.shape.color
}).openapi("PatchProjectTag");
var createViewSchema = viewSchema.omit({
  id: true,
  created: true
}).openapi("CreateView");
var patchViewSchema = z8.object({
  object_type: viewSchema.shape.object_type,
  object_id: viewSchema.shape.object_id,
  view_type: viewSchema.shape.view_type.nullish(),
  name: viewSchema.shape.name.nullish(),
  view_data: viewSchema.shape.view_data,
  options: viewSchema.shape.options,
  user_id: viewSchema.shape.user_id
}).openapi("PatchView");
var deleteViewSchema = z8.object({
  object_type: viewSchema.shape.object_type,
  object_id: viewSchema.shape.object_id
}).openapi("DeleteView");
var patchOrganizationSchema = z8.object({
  name: organizationSchema.shape.name.nullish(),
  api_url: organizationSchema.shape.api_url.nullish(),
  is_universal_api: organizationSchema.shape.is_universal_api.nullish(),
  proxy_url: organizationSchema.shape.proxy_url.nullish(),
  realtime_url: organizationSchema.shape.realtime_url.nullish()
}).openapi("PatchOrganization");
var createApiKeyBaseSchema = generateBaseTableOpSchema("API key");
var createApiKeySchema = z8.object({
  name: z8.string().describe("Name of the api key. Does not have to be unique"),
  org_name: createApiKeyBaseSchema.shape.org_name
});
var createApiKeyOutputSchema = apiKeySchema.merge(
  z8.object({
    key: z8.string().describe("The raw API key. It will only be exposed this one time")
  })
).openapi("CreateApiKeyOutput");
var organizationMembersSchema = z8.object({
  members: userSchema.pick({ id: true, email: true }).array()
}).openapi("OrganizationMembers");
var patchOrganizationMembersSchema = z8.object({
  invite_users: z8.object({
    ids: userSchema.shape.id.array().nullish().describe("Ids of existing users to invite"),
    emails: userSchema.shape.email.unwrap().unwrap().array().nullish().describe("Emails of users to invite"),
    send_invite_emails: z8.boolean().nullish().describe(
      "If true, send invite emails to the users who wore actually added"
    ),
    group_id: groupSchema.shape.id.nullish().describe(
      "Optional id of a group to add newly-invited users to. Cannot specify both a group id and a group name."
    ),
    group_name: groupSchema.shape.name.nullish().describe(
      "Optional name of a group to add newly-invited users to. Cannot specify both a group id and a group name."
    )
  }).nullish().describe("Users to invite to the organization"),
  remove_users: z8.object({
    ids: userSchema.shape.id.array().nullish().describe("Ids of users to remove"),
    emails: userSchema.shape.email.unwrap().unwrap().array().nullish().describe("Emails of users to remove")
  }).nullish().describe("Users to remove from the organization"),
  org_name: z8.string().nullish().describe(
    `For nearly all users, this parameter should be unnecessary. But in the rare case that your API key belongs to multiple organizations, or in case you want to explicitly assert the organization you are modifying, you may specify the name of the organization.`
  ),
  org_id: z8.string().nullish().describe(
    `For nearly all users, this parameter should be unnecessary. But in the rare case that your API key belongs to multiple organizations, or in case you want to explicitly assert the organization you are modifying, you may specify the id of the organization.`
  )
}).openapi("PatchOrganizationMembers");
var patchOrganizationMembersOutputSchema = z8.object({
  status: z8.literal("success"),
  send_email_error: z8.string().nullish().describe(
    "If invite emails failed to send for some reason, the patch operation will still complete, but we will return an error message here"
  )
});
var apiSpecObjectSchemas = {
  experiment: {
    object: experimentSchema,
    create: createExperimentSchema,
    patch_id: patchExperimentSchema
  },
  dataset: {
    object: datasetSchema,
    create: createDatasetSchema,
    patch_id: patchDatasetSchema
  },
  project: {
    object: projectSchema,
    create: createProjectSchema,
    patch_id: patchProjectSchema
  },
  prompt: {
    object: promptSchema,
    create: createPromptSchema,
    patch_id: patchPromptSchema
  },
  function: {
    object: functionSchema2,
    create: createFunctionSchema,
    patch_id: patchFunctionSchema
  },
  role: {
    object: roleSchema,
    create: createRoleSchema,
    patch_id: patchRoleSchema
  },
  group: {
    object: groupSchema,
    create: createGroupSchema,
    patch_id: patchGroupSchema
  },
  acl: {
    object: aclSchema,
    create: aclItemSchema,
    delete: aclItemSchema
  },
  user: {
    object: userSchema
  },
  prompt_session: {},
  project_score: {
    object: projectScoreSchema,
    create: createProjectScoreSchema,
    patch_id: patchProjectScoreSchema
  },
  project_tag: {
    object: projectTagSchema,
    create: createProjectTagSchema,
    patch_id: patchProjectTagSchema
  },
  view: {
    object: viewSchema,
    delete_id: deleteViewSchema,
    create: createViewSchema,
    patch_id: patchViewSchema
  },
  organization: {
    object: organizationSchema,
    patch_id: patchOrganizationSchema
  },
  api_key: {
    object: apiKeySchema,
    create: createApiKeySchema
  }
};

// src/string_util.ts
function capitalize(s, sep) {
  const items = sep ? s.split(sep) : [s];
  return items.map((s2) => s2 ? s2.charAt(0).toUpperCase() + s2.slice(1) : s2).join(sep || "");
}

// src/db_fields.ts
var TRANSACTION_ID_FIELD = "_xact_id";
var OBJECT_DELETE_FIELD = "_object_delete";
var IS_MERGE_FIELD = "_is_merge";
var MERGE_PATHS_FIELD = "_merge_paths";
var VALID_SOURCES = ["app", "api", "external"];
var PARENT_ID_FIELD = "_parent_id";

// src/span_types.ts
var spanTypeAttributeValues = [
  "llm",
  "score",
  "function",
  "eval",
  "task",
  "tool"
];

// src/zod_util.ts
import { z as z9 } from "zod";
function objectNullish(object) {
  return new z9.ZodObject({
    ...object._def,
    shape: () => Object.fromEntries(
      Object.entries(object.shape).map(([k, v]) => [k, v.nullish()])
    )
  });
}

// typespecs/api_types.ts
extendZodWithOpenApi4(z10);
var auditSourcesSchema = z10.enum(VALID_SOURCES);
function generateBaseEventOpSchema(objectType) {
  const eventDescription = getEventObjectDescription(objectType);
  return z10.object({
    id: z10.string().describe(
      `A unique identifier for the ${eventDescription} event. If you don't provide one, BrainTrust will generate one for you`
    ),
    [TRANSACTION_ID_FIELD]: z10.string().describe(
      `The transaction id of an event is unique to the network operation that processed the event insertion. Transaction ids are monotonically increasing over time and can be used to retrieve a versioned snapshot of the ${eventDescription} (see the \`version\` parameter)`
    ),
    created: datetimeStringSchema.describe(
      `The timestamp the ${eventDescription} event was created`
    ),
    input: customTypes.any,
    output: customTypes.any,
    expected: customTypes.any,
    error: customTypes.any.describe("The error that occurred, if any."),
    tags: z10.array(z10.string()).nullish().describe("A list of tags to log"),
    scores: z10.record(z10.number().min(0).max(1).nullish()).nullish(),
    metadata: z10.record(customTypes.any).nullish().describe(
      "A dictionary with additional data about the test example, model outputs, or just about anything else that's relevant, that you can use to help find and analyze examples later. For example, you could log the `prompt`, example's `id`, or anything else that would be useful to slice/dice later. The values in `metadata` can be any JSON-serializable type, but its keys must be strings"
    ),
    metrics: z10.object({
      start: z10.number().nullish().describe(
        `A unix timestamp recording when the section of code which produced the ${eventDescription} event started`
      ),
      end: z10.number().nullish().describe(
        `A unix timestamp recording when the section of code which produced the ${eventDescription} event finished`
      ),
      prompt_tokens: z10.number().int().nullish().describe(
        `The number of tokens in the prompt used to generate the ${eventDescription} event (only set if this is an LLM span)`
      ),
      completion_tokens: z10.number().int().nullish().describe(
        `The number of tokens in the completion generated by the model (only set if this is an LLM span)`
      ),
      tokens: z10.number().int().nullish().describe(
        `The total number of tokens in the input and output of the ${eventDescription} event.`
      )
    }).catchall(customTypes.any).nullish().describe(
      `Metrics are numerical measurements tracking the execution of the code that produced the ${eventDescription} event. Use "start" and "end" to track the time span over which the ${eventDescription} event was produced`
    ),
    context: z10.object({
      caller_functionname: z10.string().nullish().describe(
        `The function in code which created the ${eventDescription} event`
      ),
      caller_filename: z10.string().nullish().describe(
        `Name of the file in code where the ${eventDescription} event was created`
      ),
      caller_lineno: z10.number().int().nullish().describe(
        `Line of code where the ${eventDescription} event was created`
      )
    }).catchall(customTypes.any).nullish().describe(
      `Context is additional information about the code that produced the ${eventDescription} event. It is essentially the textual counterpart to \`metrics\`. Use the \`caller_*\` attributes to track the location in code which produced the ${eventDescription} event`
    ),
    span_id: z10.string().describe(
      `A unique identifier used to link different ${eventDescription} events together as part of a full trace. See the [tracing guide](https://www.braintrust.dev/docs/guides/tracing) for full details on tracing`
    ),
    span_parents: z10.string().array().nullish().describe(
      `An array of the parent \`span_ids\` of this ${eventDescription} event. This should be empty for the root span of a trace, and should most often contain just one parent element for subspans`
    ),
    root_span_id: z10.string().describe(
      `The \`span_id\` of the root of the trace this ${eventDescription} event belongs to`
    ),
    span_attributes: z10.object({
      name: z10.string().nullish().describe("Name of the span, for display purposes only"),
      type: z10.enum(spanTypeAttributeValues).nullish().describe("Type of the span, for display purposes only")
    }).catchall(customTypes.any).nullish().describe(
      "Human-identifying attributes of the span, such as name, type, etc."
    ),
    [OBJECT_DELETE_FIELD]: z10.boolean().nullish().describe(
      `Pass \`${OBJECT_DELETE_FIELD}=true\` to mark the ${eventDescription} event deleted. Deleted events will not show up in subsequent fetches for this ${eventDescription}`
    )
  });
}
function generateBaseEventFeedbackSchema(objectType) {
  const eventObjectType = getEventObjectType(objectType);
  const eventDescription = getEventObjectDescription(objectType);
  return z10.object({
    id: z10.string().describe(
      `The id of the ${eventDescription} event to log feedback for. This is the row \`id\` returned by \`POST /v1/${eventObjectType}/{${objectType}_id}/insert\``
    ),
    scores: z10.record(z10.number().min(0).max(1).nullish()).nullish().describe(
      `A dictionary of numeric values (between 0 and 1) to log. These scores will be merged into the existing scores for the ${eventDescription} event`
    ),
    expected: customTypes.any.describe(
      "The ground truth value (an arbitrary, JSON serializable object) that you'd compare to `output` to determine if your `output` value is correct or not"
    ),
    tags: z10.array(z10.string()).nullish().describe("A list of tags to log"),
    comment: z10.string().nullish().describe(
      `An optional comment string to log about the ${eventDescription} event`
    ),
    metadata: z10.record(customTypes.any).nullish().describe(
      "A dictionary with additional data about the feedback. If you have a `user_id`, you can log it here and access it in the Braintrust UI."
    ),
    source: auditSourcesSchema.nullish().describe(
      'The source of the feedback. Must be one of "external" (default), "app", or "api"'
    )
  });
}
var fetchLimitSchema = z10.number().int().nonnegative().describe(
  [
    "limit the number of traces fetched",
    `Fetch queries may be paginated if the total result size is expected to be large (e.g. project_logs which accumulate over a long time). Note that fetch queries only support pagination in descending time order (from latest to earliest \`${TRANSACTION_ID_FIELD}\`. Furthermore, later pages may return rows which showed up in earlier pages, except with an earlier \`${TRANSACTION_ID_FIELD}\`. This happens because pagination occurs over the whole version history of the event log. You will most likely want to exclude any such duplicate, outdated rows (by \`id\`) from your combined result set.`,
    `The \`limit\` parameter controls the number of full traces to return. So you may end up with more individual rows than the specified limit if you are fetching events containing traces.`
  ].join("\n\n")
);
var fetchPaginationCursorDescription = [
  "DEPRECATION NOTICE: The manually-constructed pagination cursor is deprecated in favor of the explicit 'cursor' returned by object fetch requests. Please prefer the 'cursor' argument going forwards.",
  "Together, `max_xact_id` and `max_root_span_id` form a pagination cursor",
  `Since a paginated fetch query returns results in order from latest to earliest, the cursor for the next page can be found as the row with the minimum (earliest) value of the tuple \`(${TRANSACTION_ID_FIELD}, root_span_id)\`. See the documentation of \`limit\` for an overview of paginating fetch queries.`
].join("\n\n");
var maxXactIdSchema = z10.string().describe(fetchPaginationCursorDescription);
var maxRootSpanIdSchema = z10.string().describe(fetchPaginationCursorDescription);
var fetchPaginationCursorSchema = z10.string().describe(
  [
    "An opaque string to be used as a cursor for the next page of results, in order from latest to earliest.",
    "The string can be obtained directly from the `cursor` property of the previous fetch query"
  ].join("\n\n")
);
var versionSchema = z10.string().describe(
  [
    "Retrieve a snapshot of events from a past time",
    "The version id is essentially a filter on the latest event transaction id. You can use the `max_xact_id` returned by a past fetch as the version to reproduce that exact fetch."
  ].join("\n\n")
);
var pathTypeFilterSchema = z10.object({
  type: z10.literal("path_lookup").describe("Denotes the type of filter as a path-lookup filter"),
  path: z10.string().array().describe(
    'List of fields describing the path to the value to be checked against. For instance, if you wish to filter on the value of `c` in `{"input": {"a": {"b": {"c": "hello"}}}}`, pass `path=["input", "a", "b", "c"]`'
  ),
  value: customTypes.any.describe(
    'The value to compare equality-wise against the event value at the specified `path`. The value must be a "primitive", that is, any JSON-serializable object except for objects and arrays. For instance, if you wish to filter on the value of "input.a.b.c" in the object `{"input": {"a": {"b": {"c": "hello"}}}}`, pass `value="hello"`'
  )
}).describe(
  'A path-lookup filter describes an equality comparison against a specific sub-field in the event row. For instance, if you wish to filter on the value of `c` in `{"input": {"a": {"b": {"c": "hello"}}}}`, pass `path=["input", "a", "b", "c"]` and `value="hello"`'
).openapi("PathLookupFilter");
var fetchFiltersSchema = pathTypeFilterSchema.array().describe(
  [
    "NOTE: This parameter is deprecated and will be removed in a future revision. Consider using the `/btql` endpoint (https://www.braintrust.dev/docs/reference/btql) for more advanced filtering.",
    "A list of filters on the events to fetch. Currently, only path-lookup type filters are supported."
  ].join("\n\n")
).openapi("FetchEventsFilters");
var fetchEventsRequestSchema = z10.object({
  limit: fetchLimitSchema.nullish(),
  cursor: fetchPaginationCursorSchema.nullish(),
  max_xact_id: maxXactIdSchema.nullish(),
  max_root_span_id: maxRootSpanIdSchema.nullish(),
  filters: fetchFiltersSchema.nullish(),
  version: versionSchema.nullish()
}).openapi("FetchEventsRequest");
function makeFetchEventsResponseSchema(objectType, eventSchema) {
  const eventName = capitalize(getEventObjectType(objectType), "_").replace(
    "_",
    ""
  );
  return z10.object({
    events: eventSchema.array().describe("A list of fetched events"),
    cursor: z10.string().nullish().describe(
      [
        "Pagination cursor",
        "Pass this string directly as the `cursor` param to your next fetch request to get the next page of results. Not provided if the returned result set is empty."
      ].join("\n\n")
    )
  }).openapi(`Fetch${eventName}EventsResponse`);
}
var experimentEventBaseSchema = generateBaseEventOpSchema("experiment");
var experimentEventSchema = z10.object({
  id: experimentEventBaseSchema.shape.id,
  dataset_record_id: z10.string().nullish().describe(
    "If the experiment is associated to a dataset, this is the event-level dataset id this experiment event is tied to"
  ),
  [TRANSACTION_ID_FIELD]: experimentEventBaseSchema.shape[TRANSACTION_ID_FIELD],
  created: experimentEventBaseSchema.shape.created,
  project_id: experimentSchema.shape.project_id,
  experiment_id: experimentSchema.shape.id,
  input: experimentEventBaseSchema.shape.input.describe(
    "The arguments that uniquely define a test case (an arbitrary, JSON serializable object). Later on, Braintrust will use the `input` to know whether two test cases are the same between experiments, so they should not contain experiment-specific state. A simple rule of thumb is that if you run the same experiment twice, the `input` should be identical"
  ),
  output: experimentEventBaseSchema.shape.output.describe(
    "The output of your application, including post-processing (an arbitrary, JSON serializable object), that allows you to determine whether the result is correct or not. For example, in an app that generates SQL queries, the `output` should be the _result_ of the SQL query generated by the model, not the query itself, because there may be multiple valid queries that answer a single question"
  ),
  expected: experimentEventBaseSchema.shape.expected.describe(
    "The ground truth value (an arbitrary, JSON serializable object) that you'd compare to `output` to determine if your `output` value is correct or not. Braintrust currently does not compare `output` to `expected` for you, since there are so many different ways to do that correctly. Instead, these values are just used to help you navigate your experiments while digging into analyses. However, we may later use these values to re-score outputs or fine-tune your models"
  ),
  error: experimentEventBaseSchema.shape.error,
  scores: experimentEventBaseSchema.shape.scores.describe(
    "A dictionary of numeric values (between 0 and 1) to log. The scores should give you a variety of signals that help you determine how accurate the outputs are compared to what you expect and diagnose failures. For example, a summarization app might have one score that tells you how accurate the summary is, and another that measures the word similarity between the generated and grouth truth summary. The word similarity score could help you determine whether the summarization was covering similar concepts or not. You can use these scores to help you sort, filter, and compare experiments"
  ),
  metadata: experimentEventBaseSchema.shape.metadata,
  tags: experimentEventBaseSchema.shape.tags,
  metrics: experimentEventBaseSchema.shape.metrics,
  context: experimentEventBaseSchema.shape.context,
  span_id: experimentEventBaseSchema.shape.span_id,
  span_parents: experimentEventBaseSchema.shape.span_parents,
  root_span_id: experimentEventBaseSchema.shape.root_span_id,
  span_attributes: experimentEventBaseSchema.shape.span_attributes
}).openapi("ExperimentEvent");
var datasetEventBaseSchema = generateBaseEventOpSchema("dataset");
var datasetEventSchema = z10.object({
  id: datasetEventBaseSchema.shape.id,
  [TRANSACTION_ID_FIELD]: datasetEventBaseSchema.shape[TRANSACTION_ID_FIELD],
  created: datasetEventBaseSchema.shape.created,
  project_id: datasetSchema.shape.project_id,
  dataset_id: datasetSchema.shape.id,
  input: datasetEventBaseSchema.shape.input.describe(
    "The argument that uniquely define an input case (an arbitrary, JSON serializable object)"
  ),
  expected: datasetEventBaseSchema.shape.expected.describe(
    "The output of your application, including post-processing (an arbitrary, JSON serializable object)"
  ),
  metadata: datasetEventBaseSchema.shape.metadata,
  tags: datasetEventBaseSchema.shape.tags,
  span_id: datasetEventBaseSchema.shape.span_id,
  root_span_id: datasetEventBaseSchema.shape.root_span_id
}).openapi("DatasetEvent");
var promptSessionEventBaseSchema = generateBaseEventOpSchema("prompt_session");
var promptSessionEventSchema = z10.object({
  id: promptSessionEventBaseSchema.shape.id,
  [TRANSACTION_ID_FIELD]: promptSessionEventBaseSchema.shape[TRANSACTION_ID_FIELD],
  created: promptSessionEventBaseSchema.shape.created,
  project_id: promptSchema.shape.project_id,
  prompt_session_id: promptSchema.shape.id,
  prompt_session_data: customTypes.any.describe(
    "Data about the prompt session"
  ),
  prompt_data: customTypes.any.describe("Data about the prompt"),
  object_data: customTypes.any.describe("Data about the mapped data"),
  completion: customTypes.any.describe("Data about the completion"),
  tags: promptSessionEventBaseSchema.shape.tags
}).openapi("PromptSessionEvent");
var projectLogsEventBaseSchema = generateBaseEventOpSchema("project");
var projectLogsLogIdLiteralSchema = z10.literal("g").describe("A literal 'g' which identifies the log as a project log");
var projectLogsEventSchema = z10.object({
  id: projectLogsEventBaseSchema.shape.id,
  [TRANSACTION_ID_FIELD]: projectLogsEventBaseSchema.shape[TRANSACTION_ID_FIELD],
  created: projectLogsEventBaseSchema.shape.created,
  org_id: projectSchema.shape.org_id,
  project_id: projectSchema.shape.id,
  log_id: projectLogsLogIdLiteralSchema,
  input: projectLogsEventBaseSchema.shape.input.describe(
    "The arguments that uniquely define a user input (an arbitrary, JSON serializable object)."
  ),
  output: projectLogsEventBaseSchema.shape.output.describe(
    "The output of your application, including post-processing (an arbitrary, JSON serializable object), that allows you to determine whether the result is correct or not. For example, in an app that generates SQL queries, the `output` should be the _result_ of the SQL query generated by the model, not the query itself, because there may be multiple valid queries that answer a single question."
  ),
  expected: projectLogsEventBaseSchema.shape.expected.describe(
    "The ground truth value (an arbitrary, JSON serializable object) that you'd compare to `output` to determine if your `output` value is correct or not. Braintrust currently does not compare `output` to `expected` for you, since there are so many different ways to do that correctly. Instead, these values are just used to help you navigate while digging into analyses. However, we may later use these values to re-score outputs or fine-tune your models."
  ),
  error: projectLogsEventBaseSchema.shape.error,
  scores: projectLogsEventBaseSchema.shape.scores.describe(
    "A dictionary of numeric values (between 0 and 1) to log. The scores should give you a variety of signals that help you determine how accurate the outputs are compared to what you expect and diagnose failures. For example, a summarization app might have one score that tells you how accurate the summary is, and another that measures the word similarity between the generated and grouth truth summary. The word similarity score could help you determine whether the summarization was covering similar concepts or not. You can use these scores to help you sort, filter, and compare logs."
  ),
  metadata: projectLogsEventBaseSchema.shape.metadata,
  tags: projectLogsEventBaseSchema.shape.tags,
  metrics: projectLogsEventBaseSchema.shape.metrics,
  context: projectLogsEventBaseSchema.shape.context,
  span_id: projectLogsEventBaseSchema.shape.span_id,
  span_parents: projectLogsEventBaseSchema.shape.span_parents,
  root_span_id: projectLogsEventBaseSchema.shape.root_span_id,
  span_attributes: projectLogsEventBaseSchema.shape.span_attributes
}).openapi("ProjectLogsEvent");
var isMergeDescription = [
  "The `_is_merge` field controls how the row is merged with any existing row with the same id in the DB. By default (or when set to `false`), the existing row is completely replaced by the new row. When set to `true`, the new row is deep-merged into the existing row",
  'For example, say there is an existing row in the DB `{"id": "foo", "input": {"a": 5, "b": 10}}`. If we merge a new row as `{"_is_merge": true, "id": "foo", "input": {"b": 11, "c": 20}}`, the new row will be `{"id": "foo", "input": {"a": 5, "b": 11, "c": 20}}`. If we replace the new row as `{"id": "foo", "input": {"b": 11, "c": 20}}`, the new row will be `{"id": "foo", "input": {"b": 11, "c": 20}}`'
].join("\n\n");
var mergeEventSchema = z10.object({
  [IS_MERGE_FIELD]: customTypes.literalTrue.describe(isMergeDescription),
  [MERGE_PATHS_FIELD]: z10.string().array().array().nullish().describe(
    [
      "The `_merge_paths` field allows controlling the depth of the merge. It can only be specified alongside `_is_merge=true`. `_merge_paths` is a list of paths, where each path is a list of field names. The deep merge will not descend below any of the specified merge paths.",
      'For example, say there is an existing row in the DB `{"id": "foo", "input": {"a": {"b": 10}, "c": {"d": 20}}, "output": {"a": 20}}`. If we merge a new row as `{"_is_merge": true, "_merge_paths": [["input", "a"], ["output"]], "input": {"a": {"q": 30}, "c": {"e": 30}, "bar": "baz"}, "output": {"d": 40}}`, the new row will be `{"id": "foo": "input": {"a": {"q": 30}, "c": {"d": 20, "e": 30}, "bar": "baz"}, "output": {"d": 40}}`. In this case, due to the merge paths, we have replaced `input.a` and `output`, but have still deep-merged `input` and `input.c`.'
    ].join("\n\n")
  )
});
var replacementEventSchema = z10.object({
  [IS_MERGE_FIELD]: customTypes.literalFalse.nullish().describe(isMergeDescription),
  [PARENT_ID_FIELD]: z10.string().nullish().describe(
    [
      "Use the `_parent_id` field to create this row as a subspan of an existing row. It cannot be specified alongside `_is_merge=true`. Tracking hierarchical relationships are important for tracing (see the [guide](https://www.braintrust.dev/docs/guides/tracing) for full details).",
      'For example, say we have logged a row `{"id": "abc", "input": "foo", "output": "bar", "expected": "boo", "scores": {"correctness": 0.33}}`. We can create a sub-span of the parent row by logging `{"_parent_id": "abc", "id": "llm_call", "input": {"prompt": "What comes after foo?"}, "output": "bar", "metrics": {"tokens": 1}}`. In the webapp, only the root span row `"abc"` will show up in the summary view. You can view the full trace hierarchy (in this case, the `"llm_call"` row) by clicking on the "abc" row.'
    ].join("\n\n")
  )
});
function makeInsertEventSchemas(objectType, insertSchema) {
  const eventDescription = getEventObjectDescription(objectType);
  const article = getObjectArticle(objectType);
  const eventSchemaName = capitalize(
    getEventObjectType(objectType),
    "_"
  ).replace("_", "");
  const replaceVariantSchema = insertSchema.merge(replacementEventSchema).openapi(`Insert${eventSchemaName}EventReplace`);
  const mergeVariantSchema = insertSchema.merge(mergeEventSchema).openapi(`Insert${eventSchemaName}EventMerge`);
  const eventSchema = z10.union([replaceVariantSchema, mergeVariantSchema]).describe(`${capitalize(article)} ${eventDescription} event`).openapi(`Insert${eventSchemaName}Event`);
  const requestSchema = z10.object({
    events: eventSchema.array().describe(`A list of ${eventDescription} events to insert`)
  }).openapi(`Insert${eventSchemaName}EventRequest`);
  return { eventSchema, requestSchema };
}
var insertEventsResponseSchema = z10.object({
  row_ids: z10.string().array().describe(
    "The ids of all rows that were inserted, aligning one-to-one with the rows provided as input"
  )
}).openapi("InsertEventsResponse");
var insertExperimentEventBaseSchema = objectNullish(
  experimentEventSchema.pick({
    input: true,
    output: true,
    expected: true,
    error: true,
    scores: true,
    metadata: true,
    tags: true,
    metrics: true,
    context: true,
    span_attributes: true,
    id: true,
    dataset_record_id: true,
    created: true
  }).extend({
    [OBJECT_DELETE_FIELD]: experimentEventBaseSchema.shape[OBJECT_DELETE_FIELD]
  })
);
var {
  eventSchema: insertExperimentEventSchema,
  requestSchema: insertExperimentEventsRequestSchema
} = makeInsertEventSchemas("experiment", insertExperimentEventBaseSchema);
var insertDatasetEventBaseSchema = objectNullish(
  datasetEventSchema.pick({
    input: true,
    expected: true,
    metadata: true,
    tags: true,
    id: true,
    created: true
  }).extend({
    [OBJECT_DELETE_FIELD]: datasetEventBaseSchema.shape[OBJECT_DELETE_FIELD]
  })
);
var {
  eventSchema: insertDatasetEventSchema,
  requestSchema: insertDatasetEventsRequestSchema
} = makeInsertEventSchemas("dataset", insertDatasetEventBaseSchema);
var insertProjectLogsEventBaseSchema = objectNullish(
  projectLogsEventSchema.pick({
    input: true,
    output: true,
    expected: true,
    error: true,
    scores: true,
    metadata: true,
    tags: true,
    metrics: true,
    context: true,
    span_attributes: true,
    id: true,
    created: true
  }).extend({
    [OBJECT_DELETE_FIELD]: projectLogsEventBaseSchema.shape[OBJECT_DELETE_FIELD]
  })
);
var {
  eventSchema: insertProjectLogsEventSchema,
  requestSchema: insertProjectLogsEventsRequestSchema
} = makeInsertEventSchemas("project", insertProjectLogsEventBaseSchema);
function makeFeedbackRequestSchema(objectType, feedbackSchema) {
  const eventDescription = getEventObjectDescription(objectType);
  const eventSchemaName = capitalize(
    getEventObjectType(objectType),
    "_"
  ).replace("_", "");
  return z10.object({
    feedback: feedbackSchema.array().describe(`A list of ${eventDescription} feedback items`)
  }).openapi(`Feedback${eventSchemaName}EventRequest`);
}
var feedbackExperimentRequestBaseSchema = generateBaseEventFeedbackSchema("experiment");
var feedbackExperimentItemSchema = feedbackExperimentRequestBaseSchema.pick({
  id: true,
  scores: true,
  expected: true,
  comment: true,
  metadata: true,
  source: true
}).openapi("FeedbackExperimentItem");
var feedbackExperimentRequestSchema = makeFeedbackRequestSchema(
  "experiment",
  feedbackExperimentItemSchema
);
var feedbackDatasetRequestBaseSchema = generateBaseEventFeedbackSchema("dataset");
var feedbackDatasetItemSchema = feedbackDatasetRequestBaseSchema.pick({
  id: true,
  comment: true,
  metadata: true,
  source: true
}).openapi("FeedbackDatasetItem");
var feedbackDatasetRequestSchema = makeFeedbackRequestSchema(
  "dataset",
  feedbackDatasetItemSchema
);
var feedbackProjectLogsRequestBaseSchema = generateBaseEventFeedbackSchema("project");
var feedbackProjectLogsItemSchema = feedbackProjectLogsRequestBaseSchema.pick({
  id: true,
  scores: true,
  expected: true,
  comment: true,
  metadata: true,
  source: true
}).openapi("FeedbackProjectLogsItem");
var feedbackProjectLogsRequestSchema = makeFeedbackRequestSchema(
  "project",
  feedbackProjectLogsItemSchema
);
var apiSpecEventObjectSchemas = {
  experiment: {
    event: experimentEventSchema,
    fetchResponse: makeFetchEventsResponseSchema(
      "experiment",
      experimentEventSchema
    ),
    insertEvent: insertExperimentEventSchema,
    insertRequest: insertExperimentEventsRequestSchema,
    feedbackItem: feedbackExperimentItemSchema,
    feedbackRequest: feedbackExperimentRequestSchema
  },
  dataset: {
    event: datasetEventSchema,
    fetchResponse: makeFetchEventsResponseSchema("dataset", datasetEventSchema),
    insertEvent: insertDatasetEventSchema,
    insertRequest: insertDatasetEventsRequestSchema,
    feedbackItem: feedbackDatasetItemSchema,
    feedbackRequest: feedbackDatasetRequestSchema
  },
  project_logs: {
    event: projectLogsEventSchema,
    fetchResponse: makeFetchEventsResponseSchema(
      "project",
      projectLogsEventSchema
    ),
    insertEvent: insertProjectLogsEventSchema,
    insertRequest: insertProjectLogsEventsRequestSchema,
    feedbackItem: feedbackProjectLogsItemSchema,
    feedbackRequest: feedbackProjectLogsRequestSchema
  },
  prompt: {
    event: promptSchema
  },
  function: {
    event: functionSchema2
  },
  prompt_session: {}
};
function makeCrossObjectIndividualRequestSchema(objectType) {
  const eventObjectType = getEventObjectType(objectType);
  const eventDescription = getEventObjectDescription(objectType);
  const eventObjectSchema = apiSpecEventObjectSchemas[eventObjectType];
  const insertObject = z10.object({
    ...eventObjectSchema.insertEvent ? {
      events: eventObjectSchema.insertEvent.array().nullish().describe(`A list of ${eventDescription} events to insert`)
    } : {},
    ...eventObjectSchema.feedbackItem ? {
      feedback: eventObjectSchema.feedbackItem.array().nullish().describe(`A list of ${eventDescription} feedback items`)
    } : {}
  });
  return z10.record(z10.string().uuid(), insertObject).nullish().describe(
    `A mapping from ${objectType} id to a set of log events and feedback items to insert`
  );
}
function makeCrossObjectIndividualResponseSchema(objectType) {
  return z10.record(z10.string().uuid(), insertEventsResponseSchema).nullish().describe(
    `A mapping from ${objectType} id to row ids for inserted \`events\``
  );
}
var crossObjectInsertRequestSchema = z10.object({
  experiment: makeCrossObjectIndividualRequestSchema("experiment"),
  dataset: makeCrossObjectIndividualRequestSchema("dataset"),
  project_logs: makeCrossObjectIndividualRequestSchema("project")
}).openapi("CrossObjectInsertRequest");
var crossObjectInsertResponseSchema = z10.object({
  experiment: makeCrossObjectIndividualResponseSchema("experiment"),
  dataset: makeCrossObjectIndividualResponseSchema("dataset"),
  project_logs: makeCrossObjectIndividualResponseSchema("project")
}).openapi("CrossObjectInsertResponse");
var summarizeScoresParamSchema = z10.boolean().describe(
  "Whether to summarize the scores and metrics. If false (or omitted), only the metadata will be returned."
);
var comparisonExperimentIdParamSchema = z10.string().uuid().describe(
  "The experiment to compare against, if summarizing scores and metrics. If omitted, will fall back to the `base_exp_id` stored in the experiment metadata, and then to the most recent experiment run in the same project. Must pass `summarize_scores=true` for this id to be used"
);
var summarizeDataParamSchema = z10.boolean().describe(
  "Whether to summarize the data. If false (or omitted), only the metadata will be returned."
);
var summarizeExperimentResponseSchema = z10.object({
  project_name: z10.string().describe("Name of the project that the experiment belongs to"),
  experiment_name: z10.string().describe("Name of the experiment"),
  project_url: z10.string().url().describe("URL to the project's page in the Braintrust app"),
  experiment_url: z10.string().url().describe("URL to the experiment's page in the Braintrust app"),
  comparison_experiment_name: z10.string().nullish().describe("The experiment which scores are baselined against"),
  scores: z10.record(
    z10.object({
      name: z10.string().describe("Name of the score"),
      score: z10.number().min(0).max(1).describe("Average score across all examples"),
      diff: z10.number().min(-1).max(1).optional().describe(
        "Difference in score between the current and comparison experiment"
      ),
      improvements: z10.number().int().min(0).describe("Number of improvements in the score"),
      regressions: z10.number().int().min(0).describe("Number of regressions in the score")
    }).describe("Summary of a score's performance").openapi("ScoreSummary")
  ).nullish().describe("Summary of the experiment's scores"),
  metrics: z10.record(
    z10.object({
      name: z10.string().describe("Name of the metric"),
      metric: z10.number().describe("Average metric across all examples"),
      unit: z10.string().describe("Unit label for the metric"),
      diff: z10.number().optional().describe(
        "Difference in metric between the current and comparison experiment"
      ),
      improvements: z10.number().int().min(0).describe("Number of improvements in the metric"),
      regressions: z10.number().int().min(0).describe("Number of regressions in the metric")
    }).describe("Summary of a metric's performance").openapi("MetricSummary")
  ).nullish().describe("Summary of the experiment's metrics")
}).describe("Summary of an experiment").openapi("SummarizeExperimentResponse");
var summarizeDatasetResponseSchema = z10.object({
  project_name: z10.string().describe("Name of the project that the dataset belongs to"),
  dataset_name: z10.string().describe("Name of the dataset"),
  project_url: z10.string().url().describe("URL to the project's page in the Braintrust app"),
  dataset_url: z10.string().url().describe("URL to the dataset's page in the Braintrust app"),
  data_summary: z10.object({
    total_records: z10.number().int().min(0).describe("Total number of records in the dataset")
  }).nullish().describe("Summary of a dataset's data").openapi("DataSummary")
}).describe("Summary of a dataset").openapi("SummarizeDatasetResponse");
var objectTypeSummarizeResponseSchemas = {
  experiment: summarizeExperimentResponseSchema,
  dataset: summarizeDatasetResponseSchema
};
export {
  BRAINTRUST_PARAMS,
  aclBatchUpdateRequestSchema,
  aclBatchUpdateResponseSchema,
  aclItemSchema,
  aclObjectTypeEnum,
  aclSchema,
  apiKeySchema,
  apiSpecEventObjectSchemas,
  apiSpecObjectSchemas,
  appLimitSchema,
  baseSSEEventSchema,
  callEventSchema,
  chatCompletionContentPartImageSchema,
  chatCompletionMessageToolCallSchema,
  codeBundleSchema,
  comparisonExperimentIdParamSchema,
  createApiKeyOutputSchema,
  createFunctionSchema,
  createViewSchema,
  crossObjectInsertRequestSchema,
  crossObjectInsertResponseSchema,
  customTypes,
  customTypesMode,
  datasetEventSchema,
  datasetSchema,
  datetimeStringSchema,
  endingBeforeSchema,
  experimentEventSchema,
  experimentSchema,
  fetchEventsRequestSchema,
  fetchFiltersSchema,
  fetchLimitSchema,
  fetchPaginationCursorSchema,
  functionDataSchema,
  functionIdSchema,
  functionSchema2 as functionSchema,
  getEventObjectDescription,
  getEventObjectType,
  getObjectArticle,
  groupSchema,
  insertEventsResponseSchema,
  invokeApiSchema,
  invokeFunctionSchema,
  jsonSchema,
  literalSchema,
  makeObjectIdsFilterSchema,
  maxOverWindowSchema,
  maxRootSpanIdSchema,
  maxXactIdSchema,
  memberSchema,
  messageRoleSchema,
  modelParamsSchema,
  objectTypeSummarizeResponseSchemas,
  objectTypes,
  objectTypesWithEvent,
  orgSecretsSchema,
  organizationMembersSchema,
  organizationSchema,
  patchDatasetSchema,
  patchExperimentSchema,
  patchGroupSchema,
  patchOrganizationMembersOutputSchema,
  patchOrganizationMembersSchema,
  patchOrganizationSchema,
  patchProjectSchema,
  patchProjectScoreSchema,
  patchProjectTagSchema,
  patchPromptSchema,
  patchRoleSchema,
  patchViewSchema,
  permissionEnum,
  projectLogsEventSchema,
  projectLogsLogIdLiteralSchema,
  projectSchema,
  projectScoreCategory,
  projectScoreSchema,
  projectScoreTypeEnum,
  projectSettingsSchema,
  projectTagSchema,
  promptBaseSchema,
  promptBlockDataSchema,
  promptDataSchema,
  promptLogIdLiteralSchema,
  promptOptionsSchema,
  promptSchema,
  promptSessionEventSchema,
  promptSessionSchema,
  repoInfoSchema,
  resourcesSchema,
  roleSchema,
  runEvalSchema,
  runtimeContextSchema,
  sseDataEventSchema,
  sseDoneEventSchema,
  sseTextEventSchema,
  startingAfterSchema,
  summarizeDataParamSchema,
  summarizeScoresParamSchema,
  toolsSchema,
  useFunctionSchema,
  userSchema,
  validRuntimesEnum,
  versionSchema,
  viewDataSchema,
  viewDataSearchSchema,
  viewOptionsSchema,
  viewSchema,
  viewTypeEnum
};
